import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  AlertCircle,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";

import FormCard from "./FormCard";
import { useFormData } from "./formData";
import { useWallet } from "@/context/WalletContext";

export default function FillMyForm() {
  const [search, setSearch] = useState("");

  const location = useLocation();
  const { refreshWallet } = useWallet();

  const {
    forms,
    mostUsed,
    loading,
    error,
    reload,
  } = useFormData();

  /*
   * Refresh the user's wallet whenever this page opens
   * or its route location changes.
   */
  useEffect(() => {
    refreshWallet();
  }, [location.key, refreshWallet]);

  /*
   * Search forms by title or form name.
   */
  const filteredForms = useMemo(() => {
    const searchText = search
      .trim()
      .toLowerCase();

    if (!searchText) {
      return forms;
    }

    return forms.filter((form) => {
      const title = String(
        form?.title || ""
      ).toLowerCase();

      const formName = String(
        form?.name || ""
      ).toLowerCase();

      return (
        title.includes(searchText) ||
        formName.includes(searchText)
      );
    });
  }, [forms, search]);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-6 py-8">
        <h1 className="text-3xl font-bold">
          Fill My Form
        </h1>

        <p className="mt-2 text-muted-foreground">
          Choose a form to continue.
        </p>

        <div className="relative mt-8">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />

          <input
            type="text"
            placeholder="Search Forms..."
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
            className="w-full rounded-xl border bg-card py-3 pl-12 pr-4 outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {loading && (
          <div className="flex min-h-[300px] items-center justify-center">
            <div className="text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />

              <p className="mt-3 text-sm text-muted-foreground">
                Loading forms...
              </p>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="mt-8 rounded-xl border border-red-300 bg-red-50 p-5 text-red-700">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />

              <div>
                <p className="font-semibold">
                  Unable to load forms
                </p>

                <p className="mt-1 text-sm">
                  {error}
                </p>

                <button
                  type="button"
                  onClick={reload}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {mostUsed.length > 0 && !search.trim() && (
              <>
                <h2 className="mb-5 mt-10 text-xl font-semibold">
                  ⭐ Most Used
                </h2>

                <div className="grid grid-cols-2 gap-5 md:grid-cols-4 lg:grid-cols-6">
                  {mostUsed.map((item) => (
                    <FormCard
                      key={item.id}
                      form={item}
                    />
                  ))}
                </div>
              </>
            )}

            <h2 className="mb-5 mt-12 text-xl font-semibold">
              All Forms
            </h2>

            {filteredForms.length > 0 ? (
              <div className="grid grid-cols-2 gap-5 md:grid-cols-4 lg:grid-cols-6">
                {filteredForms.map((item) => (
                  <FormCard
                    key={item.id}
                    form={item}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border bg-card p-8 text-center">
                <Search className="mx-auto h-8 w-8 text-muted-foreground" />

                <p className="mt-3 font-semibold">
                  No forms found
                </p>

                <p className="mt-1 text-sm text-muted-foreground">
                  {search.trim()
                    ? "No form matches your search."
                    : "No visible forms are available."}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}